import React from 'react';
import renderValue from '../../utility/renderValue';
import classNames from '../../../../utility/classNames';
import InputTypeSwitch from '../../formElements/InputTypeSwitch';
import FormUIOptions from '../../formElements/formUIOptions';
import Tooltip from '../../../Tooltip';
import { useFormikContext } from 'formik';
import { checkCondition } from '../../utility/filterSchema';
import SectionContainer from './SectionContainer';

export default function SimpleFormBeforeAfterSection({ section, beforeData }) {
  const {
    sectionLabel,
    sectionDescription,
    sectionNoToggle,
    sectionCollapsed,
    sectionHeadersInline,
    sectionHeadersModal,
    sectionHeadersType,
    tableModalUnless,
    schema,
  } = section;

  const { values } = useFormikContext();

  const filteredSchema = schema.filter((item) => {
    if (item.condition) {
      return checkCondition(item.condition, { ...beforeData, ...values });
    }
    return true;
  });

  // there's some different formatting things we need to do, specifically for the rental income section
  const tableModal =
    sectionHeadersType === 'tableModal' && !values[tableModalUnless];

  return (
    <SectionContainer
      label={sectionLabel}
      description={sectionDescription}
      defaultOpen={!sectionCollapsed}
      toggleSection={!sectionNoToggle}
      id={section.sectionValue}
    >
      <div className="w-full mt-1 grid sm:grid-cols-[180px_2fr_1fr]">
        {!tableModal &&
          sectionHeadersInline?.map((header, index) => (
            <p
              key={header}
              scope="col"
              className={classNames(
                'hidden sm:flex flex-col border-b  bg-white sticky z-10 mt-2 pb-2 mb-3 text-md pr-2',
                index === 1
                  ? 'items-start   text-primary'
                  : 'items-end text-neutral-500 '
              )}
            >
              {Array.isArray(header) ? (
                <>
                  <span className="whitespace-nowrap">{header[0]} &#47;</span>
                  <span>{header[1]}</span>
                </>
              ) : (
                header
              )}
            </p>
          ))}

        {tableModal && (
          <div className="hidden sm:grid sm:grid-cols-4-beforeaftertablemodal col-span-full sm:-mb-6 mt-9">
            {sectionHeadersModal?.map((header, index) => (
              <div
                key={header.heading}
                scope="col"
                className={classNames(
                  'hidden sm:flex items-center border-b bg-white sticky z-10 top-16 pb-2 mb-3 text-md text-center text-neutral-900',
                  index === 1
                    ? 'justify-center'
                    : index === sectionHeadersModal.length - 2
                    ? 'font-medium'
                    : index === sectionHeadersModal.length - 1
                    ? 'justify-end text-neutral-500'
                    : 'justify-start'
                )}
              >
                <span className="mr-[2px] mb-[2px]">{header.heading}</span>
                {header.description && (
                  <Tooltip
                    text={header.description}
                    type="info"
                    classes={'z-30 shadow-none mt-1 ml-[3px]'}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {filteredSchema.map((field) => {
          const shouldShowBeforeValue =
            (!field.condition || checkCondition(field.condition, beforeData)) &&
            field.type !== FormUIOptions.YesNo;

          const beforeValue =
            field.type === FormUIOptions.TableInline ||
            field.type === FormUIOptions.TableModal ||
            !shouldShowBeforeValue
              ? null
              : renderValue(field, beforeData);

          const cellClasses = classNames(
            'px-2 sm:px-0 bg-white h-full col-span-3',
            field.type === FormUIOptions.TableInline ||
              field.type === FormUIOptions.TableModal
              ? 'w-full'
              : 'flex items-center sm:col-span-1'
          );

          const displayOriginalHeader =
            beforeValue &&
            field.type !== FormUIOptions.YesNo &&
            field.type !== FormUIOptions.Header &&
            field.type !== FormUIOptions.CalculatedSection &&
            field.type !== FormUIOptions.Calculated;
          const displayAfterHeader =
            field.type !== FormUIOptions.YesNo &&
            field.type !== FormUIOptions.Header &&
            field.type !== FormUIOptions.CalculatedSection &&
            field.type !== FormUIOptions.Calculated;

          return (
            <div
              key={field.name}
              className={classNames(
                'w-full grid col-span-3',
                tableModal
                  ? 'sm:grid-cols-[180px_1fr_245px]'
                  : 'sm:grid-cols-[180px_2fr_1fr]'
              )}
            >
              {field.groupStart && (
                <div className="w-full p-1 col-span-3">
                  <div className="h-[1px] w-full  bg-neutral-50 rounded-sm"></div>
                </div>
              )}
              {field.type !== FormUIOptions.TableInline &&
                field.type !== FormUIOptions.TableModal &&
                field.type !== FormUIOptions.CalculatedSection &&
                !field.hideLabel && (
                  <div className={classNames(cellClasses, 'py-2')}>
                    <span className="text-md font-normal text-neutral-900 whitespace-nowrap">
                      {field.label}&nbsp;
                      {field.description && (
                        <Tooltip
                          text={field.description}
                          heading={field.tooltipHeading}
                          formula={field.tooltipFormula}
                          type="info"
                        />
                      )}
                    </span>
                  </div>
                )}

              {displayAfterHeader && (
                <p
                  className={classNames(
                    'sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider invalidClass',
                    cellClasses
                  )}
                >
                  {Array.isArray(sectionHeadersInline[1]) ? (
                    <>
                      <span className="inline-block whitespace-nowrap">
                        {sectionHeadersInline[1][0]} &#47;
                      </span>
                      <span className="inline-block">
                        &nbsp;{sectionHeadersInline[1][1]}:
                      </span>
                    </>
                  ) : (
                    `${
                      field.type !== FormUIOptions.TableInline &&
                      field.type !== FormUIOptions.TableModal
                        ? sectionHeadersInline[1]
                        : ''
                    }`
                  )}
                </p>
              )}

              <div
                className={classNames(
                  cellClasses,
                  'py-2',
                  //if its the rental income table we are switching the order of the fields
                  field.type === FormUIOptions.CalculatedSection &&
                    'sm:col-span-2',
                  field.type === FormUIOptions.CalculatedSection &&
                    tableModal &&
                    'mr-0',
                  tableModal && 'order-last min-w-0',
                  field.description && 'pt-2'
                )}
              >
                <InputTypeSwitch
                  elem={{
                    ...field,
                    groupStart: false,
                    type:
                      field.type === FormUIOptions.TableInline
                        ? FormUIOptions.BeforeAfterTableInline
                        : field.type === FormUIOptions.TableModal
                        ? FormUIOptions.BeforeAfterTableModal
                        : field.type,

                    layout: 'tableNoLabel',
                  }}
                  externalData={beforeData}
                  sectionHeadersInline={sectionHeadersInline}
                  sectionHeadersModal={sectionHeadersModal}
                />
              </div>

              {field.type !== FormUIOptions.TableInline &&
                field.type !== FormUIOptions.TableModal && (
                  <>
                    <div className={classNames(cellClasses, 'py-2')}>
                      {displayOriginalHeader && (
                        <p className="sm:hidden text-base font-medium text-neutral-400 uppercase tracking-wider flex-grow">
                          {Array.isArray(sectionHeadersInline[2]) ? (
                            <>
                              <span className="inline-block whitespace-nowrap">
                                {sectionHeadersInline[2][0]} &#47;
                              </span>
                              <span className="inline-block">
                                &nbsp;{sectionHeadersInline[2][1]}:
                              </span>
                            </>
                          ) : (
                            `${sectionHeadersInline[2]}:`
                          )}
                        </p>
                      )}
                      <p
                        className={classNames(
                          'sm:w-full tracking-wider text-md text-neutral-700 mr-2',
                          tableModal ? 'text-left' : 'text-right'
                        )}
                      >
                        {beforeValue && (
                          <span className="p-1 rounded border border-neutral-50">
                            {beforeValue}
                          </span>
                        )}
                      </p>
                    </div>
                  </>
                )}
            </div>
          );
        })}
      </div>
    </SectionContainer>
  );
}
